<template>
  <transition name="fade">
    <div
      v-if="hasProgress && !hide"
      :data-auto-testid="HOMEPAGE_ELEMENTS_PROGRESS_TEST_ID"
      :data-testid="HOMEPAGE_ELEMENTS_PROGRESS_TEST_ID"
      class="hep-Progress"
    >
      <transition name="fade">
        <div v-if="!isNil(avgProgress)" class="hep-Progress_Bar">
          <IndicatorPanel :indicator-width="barWidth" class="hep-Bar" />
          <span
            :data-auto-testid="HOMEPAGE_ELEMENTS_PROGRESS_VALUE_TEST_ID"
            :data-testid="HOMEPAGE_ELEMENTS_PROGRESS_VALUE_TEST_ID"
            class="hep-Value hep-BarValue"
          >
            {{ formattedAverage }}
          </span>
        </div>
      </transition>
      <transition name="fade">
        <span
          v-if="!isNil(weeklyProgressGap)"
          v-tippy="{
            content: t('homepage.progress.last_week_change'),
            placement: 'top'
          }"
          :data-auto-testid="HOMEPAGE_ELEMENTS_PROGRESS_GAP_TEST_ID"
          :data-testid="HOMEPAGE_ELEMENTS_PROGRESS_GAP_TEST_ID"
          class="hep-Value hep-GapValue"
        >
          {{ formattedGap }}
        </span>
      </transition>
    </div>
  </transition>
</template>

<script setup>
import { isNil, isEmpty, isNull } from 'lodash'
import { computed, inject, ref, useId } from 'vue'
import { useI18n } from 'vue-i18n'

import HomePageApiHandler from '@/api/homepage'
import { SEARCH_TYPES } from '@/api/okr-elements'
import { handleError } from '@/utils/error-handling'
import {
  DEFAULT_GRADE_TYPES,
  getAvailableCustomRangeParams,
  USER_LIST_TYPES
} from '@/utils/homepage'
import { homePageStateInjectionKey } from '@/utils/injection-keys'
import { UNSELECTED_INTERVAL } from '@/utils/interval'
import {
  isTimeRangeSelectedInsteadOfInterval,
  NEW_HP_FILTERS_KEYS
} from '@/utils/okr-elements/filters'
import { MATH_SYMBOLS } from '@/utils/okr-elements/math'
import { getSimplifiedNumber, PLURALIZATION_KEYS } from '@/utils/pluralization'

import {
  HOMEPAGE_ELEMENTS_PROGRESS_GAP_TEST_ID,
  HOMEPAGE_ELEMENTS_PROGRESS_TEST_ID,
  HOMEPAGE_ELEMENTS_PROGRESS_VALUE_TEST_ID
} from '@/components/home-page/jest-helpers'
import IndicatorPanel from '@/components/ui/IndicatorPanel/IndicatorPanel'

defineOptions({
  name: 'HomePageElementsProgress'
})

const { t } = useI18n()

const requestId = useId()

const props = defineProps({
  payload: {
    type: Object,
    required: true
  },

  hide: {
    type: Boolean,
    default: false
  }
})

const avgProgress = ref(null)
const weeklyProgressGap = ref(null)

const hasProgress = computed(() => !isNil(avgProgress.value) || !isNil(weeklyProgressGap.value))

const barWidth = computed(() => {
  const value = Number(avgProgress.value)

  if (!isFinite(value)) {
    return 0
  }

  return value
})

const formatProgress = value =>
  `${getSimplifiedNumber(value)[PLURALIZATION_KEYS.SIMPLIFIED]}${MATH_SYMBOLS.PERCENT}`

const formattedAverage = computed(() => formatProgress(avgProgress.value))

const formattedGap = computed(() => {
  const value = weeklyProgressGap.value
  const formatted = formatProgress(value)

  return value > 0 ? `${MATH_SYMBOLS.PLUS}${formatted}` : formatted
})

const gapColor = computed(() => {
  const value = Number(weeklyProgressGap.value)

  if (value > 0) {
    return 'var(--grade-high-color-next)'
  }

  if (value < 0) {
    return 'var(--grade-low-color-next)'
  }

  return 'var(--dark-3)'
})

const homePageState = inject(homePageStateInjectionKey)
const homepageApi = new HomePageApiHandler()

const workspaceIds = computed(
  () => homePageState.filterValues.value[NEW_HP_FILTERS_KEYS.WORKSPACE_IDS]
)
const gradeTypes = computed(() => homePageState.filterValues.value[NEW_HP_FILTERS_KEYS.GRADE_TYPES])

const PAYLOAD_KEYS = {
  [USER_LIST_TYPES.OWNERS]: 'ownerIds',
  [USER_LIST_TYPES.FOREIGN_OBJECTIVES]: 'ownerIds',
  [USER_LIST_TYPES.METRICS_WITHOUT_PARENTS]: 'ownerIds',
  [USER_LIST_TYPES.STAKEHOLDERS]: 'stakeholderIds',
  [USER_LIST_TYPES.METRICS_WITHOUT_KPI]: 'ownerIds'
}

const DEFAULT_PAYLOAD = {
  dueDateFrom: null,
  dueDateTo: null,
  levelIds: null,
  gradeTypes: null,
  typeIds: null,
  order: [1],
  childOrder: [1],
  searchType: SEARCH_TYPES.HOMEPAGE
}

const getElementsProgressPayload = () => {
  const {
    listType,
    searchTypeId,
    selectedUserOrGroup,
    userSelected,
    selectedIntervals,
    selectedDates
  } = props.payload

  const { isTimeRangeSelected } = isTimeRangeSelectedInsteadOfInterval({
    ...selectedDates,
    intervals: selectedIntervals
  })

  return {
    workspaceIds: workspaceIds.value,
    groupIds: userSelected ? null : [selectedUserOrGroup],
    [PAYLOAD_KEYS[listType]]: userSelected ? [selectedUserOrGroup] : null,
    intervalIds: isTimeRangeSelected ? UNSELECTED_INTERVAL : selectedIntervals,
    ...DEFAULT_PAYLOAD,
    searchType: searchTypeId,
    ...getAvailableCustomRangeParams({
      selectedDates,
      intervalIds: selectedIntervals
    }),
    gradeTypes: isEmpty(gradeTypes.value) ? DEFAULT_GRADE_TYPES : gradeTypes.value
  }
}

const getElementsProgress = async () => {
  if (!isNull(avgProgress.value)) {
    avgProgress.value = null
  }

  if (!isNull(weeklyProgressGap.value)) {
    weeklyProgressGap.value = null
  }

  const payload = getElementsProgressPayload()

  try {
    const data = await homepageApi.getElementsProgress(payload, { requestId })

    avgProgress.value = isNil(data) ? null : data.avgProgress
    weeklyProgressGap.value = isNil(data) ? null : data.weeklyProgressGap
  } catch (error) {
    handleError({ error })
  }
}

defineExpose({
  refreshData: getElementsProgress
})
</script>

<style lang="scss" scoped>
.hep-Progress {
  display: flex;
  align-items: center;
  padding-right: 8px;
  gap: 8px;
  cursor: default;
}

.hep-Bar {
  width: 128px;
  flex-shrink: 0;
  --height: 8px;
}

.hep-Progress_Bar {
  display: flex;
  align-items: center;
  gap: 60px;
}

.hep-Value {
  font-size: $fs-14;
  font-weight: fw('medium');
  line-height: 20px;
  white-space: nowrap;
  width: 72px;
  text-align: right;
}

.hep-BarValue {
  color: $primary-color-next;
}

.hep-GapValue {
  color: v-bind(gapColor);
}
</style>
